// Cuenta propia: cambiar la contraseña y activar o quitar la verificación en dos pasos.
import { useState, type FormEvent } from 'react';
import { api } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { errorText, useApp } from '../../store.ts';

export function Account() {
  const me = useApp((s) => s.me);
  const setMe = useApp((s) => s.setMe);
  const notify = useApp((s) => s.notify);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      notify(t('account.passwordChanged'));
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  const enable = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await api.mfaEnable(code);
      setRecovery(r.recoveryCodes);
      setSetup(null);
      setCode('');
      setMe((await api.me()).user);
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.mfaDisable(disablePw, disableCode);
      setDisablePw('');
      setDisableCode('');
      setMe((await api.me()).user);
      notify(t('account.mfaDisabled'));
    } catch (err) {
      notify(errorText(err), true);
    }
  };

  if (!me) return null;
  return (
    <>
      <section className="section">
        <h2>{t('ws.account')}</h2>
        <div className="panel">
          <div className="field-row">
            <span className="label">{t('account.name')}</span>
            <span className="ellipsis">{me.name}</span>
          </div>
          <div className="field-row">
            <span className="label">{t('account.email')}</span>
            <span className="ellipsis">{me.email}</span>
          </div>
          <div className="field-row">
            <span className="label">{t('account.role')}</span>
            <span>{me.role === 'owner' ? t('members.owner') : t('members.member')}</span>
          </div>
        </div>
      </section>

      <section className="section">
        <h3>{t('account.password')}</h3>
        <form className="panel" onSubmit={changePassword}>
          <input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder={t('account.currentPassword')} autoComplete="current-password" required />
          <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t('account.newPassword')} autoComplete="new-password" minLength={10} required />
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="btn primary" type="submit">
              {t('account.changePassword')}
            </button>
          </div>
        </form>
      </section>

      <section className="section">
        <h3>{t('account.mfa')}</h3>
        <p className="hint">{t('account.mfaHint')}</p>
        {recovery ? (
          <div className="panel">
            <span>{t('account.recoveryTitle')}</span>
            <span className="hint">{t('account.recoveryHint')}</span>
            <div className="codes">
              {recovery.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={() => void navigator.clipboard.writeText(recovery.join('\n')).then(() => notify(t('domains.copied')))}>
                {t('domains.copy')}
              </button>
              <button type="button" className="btn primary" onClick={() => setRecovery(null)}>
                {t('account.recoverySaved')}
              </button>
            </div>
          </div>
        ) : me.totpEnabled ? (
          <form className="panel" onSubmit={disable}>
            <span className="row">
              <span className="badge green">{t('account.mfaOn')}</span>
            </span>
            <input className="input" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} placeholder={t('account.currentPassword')} required />
            <input className="input" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder={t('login.code')} inputMode="numeric" required />
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn danger" type="submit">
                {t('account.mfaDisable')}
              </button>
            </div>
          </form>
        ) : setup ? (
          <form className="panel" onSubmit={enable}>
            <span className="hint">{t('account.mfaScan')}</span>
            <div className="qr" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} />
            <span className="mono ellipsis" title={setup.secret}>
              {setup.secret}
            </span>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('login.code')} inputMode="numeric" autoComplete="one-time-code" required autoFocus />
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" type="button" onClick={() => setSetup(null)}>
                {t('common.cancel')}
              </button>
              <button className="btn primary" type="submit">
                {t('account.mfaEnable')}
              </button>
            </div>
          </form>
        ) : (
          <div className="panel">
            <div className="row">
              <span className="grow">{t('account.mfaOff')}</span>
              <button
                type="button"
                className="btn primary"
                onClick={async () => {
                  try {
                    setSetup(await api.mfaSetup());
                  } catch (err) {
                    notify(errorText(err), true);
                  }
                }}
              >
                {t('account.mfaSetup')}
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
